import {
  Button,
  Combobox,
  DateRangePicker,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
  SimpleSelect,
} from '@kalos/ui';
import { useMemo } from 'react';
import { type DateRange } from 'react-day-picker';
import { useWatch } from 'react-hook-form';

import { OPTION_ALL } from '../../../../../constants';
import { useTimesheetDepartmentListQuery } from '../../../../../hooks/react-query/useTimesheetDepartmentQuery';
import { useTransactionStatusBatchQuery } from '../../../../../hooks/react-query/useTransactionsStatusQuery';
import { useBatchUserQuery } from '../../../../../hooks/react-query/useUserQuery';
import { TransactionAccountComboboxPicker } from '../../../Pickers/newPickers/QueryPickerV2';
import { renderTransactionAccount, techniciansUserFilter } from '../../../Pickers/newPickers/utils';
import { useTransactionSearchFormContext } from './utils';

export const TransactionSearchForm = ({
  disableStatusPicker,
}: {
  disableStatusPicker?: boolean;
}) => {
  const form = useTransactionSearchFormContext();

  const timesheetDepartmentQuery = useTimesheetDepartmentListQuery({
    filter: { isActive: 1, teamOnly: false, fieldMask: ['TeamOnly'] },
    select(data) {
      return (
        data?.results.map((department) => ({
          label: `${department.value} | ${department.description}`,
          value: department.id.toString(),
        })) ?? []
      );
    },
  });
  const techniciansQuery = useBatchUserQuery({
    filters: techniciansUserFilter,
    select(data) {
      return data.results.map((technician) => ({
        label: `${technician.firstname} ${technician.lastname} (ID: ${technician.id})`,
        value: technician.id.toString(),
      }));
    },
  });
  const transactionsStatusesQuery = useTransactionStatusBatchQuery({
    select(data) {
      // Accounts payable has only 4 statuses.
      // the 4 statuses that our users were aware of were Pending, Approved, Rejected, Processed.
      // Our receipt flow uses the extra status, Pending. But for Accounts Payable, their "pending" status is a receipts "needs review".
      // its backwards and its weird, but that's what they wanted.
      const statuses: Array<{ label: string; value: string }> = [];
      data?.results?.forEach((status) => {
        switch (status.id) {
          case 2: {
            statuses.push({ label: 'Pending', value: status.id.toString() });
            break;
          }
          case 3: {
            statuses.push({ label: 'Accepted', value: status.id.toString() });
            break;
          }
          case 4: {
            statuses.push({ label: 'Rejected', value: status.id.toString() });
            break;
          }
          case 5: {
            statuses.push({ label: 'Processed', value: status.id.toString() });
            break;
          }
        }
      });
      return statuses;
    },
  });

  return (
    <form
      className="xs:grid-cols-2 grid grid-cols-1 gap-2 p-4 sm:grid-cols-3 md:gap-4 lg:grid-cols-4"
      onSubmit={form.handleSubmit(console.log)}
    >
      <FormField
        control={form.control}
        name="searchPhrase"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Search All Transactions</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Vendor, Invoice #, PO #, notes, description..." />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="costCenterId"
        render={({ field }) => {
          return (
            <FormItem>
              <FormLabel>Account</FormLabel>
              <TransactionAccountComboboxPicker
                placeholder="Search Account..."
                emptyLabel="Select Account"
                emptySearch="No Accounts found"
                selected={field.value?.toString() ?? ''}
                onSelect={(account) => {
                  field.onChange(account?.id ?? 0);
                }}
                renderItem={renderTransactionAccount}
                disabled={field.disabled}
              />
            </FormItem>
          );
        }}
      />

      <DateRangeSelector />

      <FormField
        control={form.control}
        name="departmentId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>From Department:</FormLabel>
            <SimpleSelect
              disabled={!timesheetDepartmentQuery.isSuccess}
              placeholder={OPTION_ALL}
              selectedValue={field.value?.toString() ?? ''}
              onChange={(value) => {
                field.onChange(Number(value));
              }}
              values={[{ label: OPTION_ALL, value: '0' }, ...(timesheetDepartmentQuery.data ?? [])]}
            />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="assignedEmployeeId"
        render={({ field }) => {
          return (
            <FormItem>
              <FormLabel>Select Employee</FormLabel>
              <Combobox
                onChange={(value) => {
                  field.onChange(value ? Number(value) : undefined);
                }}
                value={field.value?.toString() ?? ''}
                emptyLabel={OPTION_ALL}
                disabled={techniciansQuery.isPending}
                values={techniciansQuery.data ?? []}
                placeholder="Search Employee"
              />
            </FormItem>
          );
        }}
      />

      <FormField
        control={form.control}
        name="ownerId"
        render={({ field }) => {
          return (
            <FormItem>
              <FormLabel>Purchaser/PM</FormLabel>
              <Combobox
                onChange={(value) => {
                  field.onChange(value ? Number(value) : undefined);
                }}
                value={field.value?.toString() ?? ''}
                emptyLabel={OPTION_ALL}
                disabled={techniciansQuery.isPending}
                values={techniciansQuery.data ?? []}
                placeholder="Search Purchaser/PM"
              />
            </FormItem>
          );
        }}
      />

      <FormField
        control={form.control}
        name="statusId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Status</FormLabel>
            <SimpleSelect
              selectedValue={field.value?.toString() ?? ''}
              disabled={!transactionsStatusesQuery.isSuccess || disableStatusPicker}
              placeholder={OPTION_ALL}
              onChange={(value) => {
                field.onChange(Number(value));
              }}
              values={[
                { label: OPTION_ALL, value: '0' },
                ...(transactionsStatusesQuery.data ?? []),
              ]}
            />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="jobId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Job Number</FormLabel>
            <FormControl>
              <Input {...field} min={0} type="number" inputMode="numeric" />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="amount"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Amount</FormLabel>
            <FormControl>
              <Input {...field} type="number" step="0.01" inputMode="numeric" />
            </FormControl>
          </FormItem>
        )}
      />

      <Button
        type="reset"
        onClick={() => {
          form.reset();
        }}
        variant="outline"
        className="max-w-max self-end"
      >
        Reset
      </Button>
    </form>
  );
};

const DateRangeSelector = () => {
  const form = useTransactionSearchFormContext();

  const fromDate = useWatch({ control: form.control, name: 'dateFrom' });
  const toDate = useWatch({ control: form.control, name: 'dateTo' });
  const formValue = useMemo<DateRange | undefined>(() => {
    return {
      from: fromDate,
      to: toDate,
    };
  }, [fromDate, toDate]);

  return (
    <FormItem className="@2xl:col-span-2 row-span-1 flex flex-col justify-between">
      <FormLabel>Date within range</FormLabel>
      <DateRangePicker
        triggerClassName="justify-between"
        value={formValue}
        disabled={form.formState.disabled}
        onChange={(dateRange) => {
          if (dateRange) {
            if (dateRange.from) form.setValue('dateFrom', dateRange.from);
            if (dateRange.to) form.setValue('dateTo', dateRange.to);
          }
        }}
      />
    </FormItem>
  );
};
